import { Attachment, FlexBox, IconButton, Text } from '@dixa/lib-shared-habla';
import FileSaver from 'file-saver';
import { MouseEvent, useCallback } from 'react';

import { ajaxSend } from 'api/api';
import { getFileSize, isPreviewableAttachment } from 'app/utils/attachments';
import { actions } from 'components/shared/attachmentsPreviewModal/actions/attachmentsPreviewModal';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import styles from './attachmentsFieldList.module.scss';

type AttachmentsFieldListProps = {
  fields: any;
};

function fetchPrivateFileBlob(url: string): Promise<Blob> {
  return ajaxSend<Blob>({
    path: url,
    responseType: 'blob',
  });
}

export const AttachmentsFieldList = ({
  fields,
}: AttachmentsFieldListProps): JSX.Element | null => {
  const dispatch = useAppDispatch();

  const downloadAttachment = useCallback(
    async (fileName: string, url: string) => {
      try {
        const blob = await fetchPrivateFileBlob(url);

        FileSaver.saveAs(
          blob,
          decodeURIComponent(fileName.split('/').pop() as string),
        );
      } catch (e) {}
    },
    [],
  );

  const previewAttachment = useCallback(
    async (fileName: string, url: string) => {
      try {
        const blob = await fetchPrivateFileBlob(url);
        const blobUrl = window.URL.createObjectURL(blob);

        const allAttachments = fields.map((_field: any, index: number) => ({
          fileName: fields.get(index).name,
          url: fields.get(index).absolute_uri,
        }));

        const currentIndex = allAttachments.findIndex(
          (att: { fileName: string; url: string }) =>
            att.fileName === fileName && att.url === url,
        );

        dispatch(
          actions.attachmentsPreviewModalOpen({
            fileName,
            url: blobUrl,
            attachments: allAttachments,
            currentIndex: currentIndex >= 0 ? currentIndex : 0,
          }),
        );
      } catch (e) {}
    },
    [dispatch, fields],
  );

  const attachments = fields.map((_f: any, i: number) => fields.get(i));
  if (attachments.length === 0) return null;

  return (
    <FlexBox flexDirection="column" padding="200" gap="200">
      <Text.Paragraph variant="4" subdued>
        Attached file(s)
      </Text.Paragraph>

      <FlexBox
        flexDirection="column"
        gap="200"
        className={styles.scrollContainer}
      >
        {fields.map((_field: any, index: number) => {
          const file = fields.get(index);
          const label = file.size
            ? `${file.name} [${getFileSize(file.size)}]`
            : file.name;

          return (
            <Attachment
              key={index}
              fileName={file.name}
              label={label}
              nameClassName={styles.attachmentName}
              onPreview={
                isPreviewableAttachment(file.name)
                  ? () => previewAttachment(file.name, file.absolute_uri)
                  : undefined
              }
              onDownload={
                file.absolute_uri
                  ? () => downloadAttachment(file.name, file.absolute_uri)
                  : undefined
              }
              extraButtons={[
                <IconButton
                  key="delete"
                  variant="secondary"
                  icon="trash"
                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                    event.stopPropagation();
                    fields.remove(index);
                  }}
                />,
              ]}
            />
          );
        })}
      </FlexBox>
    </FlexBox>
  );
};
